import { getAlternativeTextCardReason } from '@wix/a11y-rules';
import { Box, Checkbox, Collapse, FormField, InputArea, Text } from '@wix/design-system';
import { Trans, useTranslation } from '@wix/fe-essentials/react';
import { ElementStatus, RuleHelperLayout, useDebounceFn } from '@wix/rules-panel-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { BiFieldNames } from '../../../../bi/types';
import { useBiLogger } from '../../../../hooks/use-bi-logger';
import { dataHooks } from '../../../../utils/datahooks';
import { getTransKeyByItemType } from '../../../../utils/get-translate-key-by-item-type';
import { mapAppDefinitionIdToItemType } from '../../../../utils/verticals/page-mapping';
import { useAltTextScreenContext } from '../drill-in/alt-text-screen/alt-text-screen-wrapper';
import { convertBooleanStatusToElementStatus, getFormStatusAndStatusMessage } from '../utils';
import EnlargedImage from './enlargedImage';
import { GenerateWithAiSuffixButton } from './generate-with-ai-suffix-button';
import { TextInImageMessage } from './text-in-image-message';
const MAX_ALT_TEXT_LENGTH = 1000;
export const AltImageRuleContent = _ref => {
  let {
    index,
    status,
    isDecorative,
    alt,
    isTextInImage,
    showLoader,
    dataHook,
    enableDecorativeImages,
    appDefinitionId,
    id,
    tpaData,
    entityId,
    url,
    enlargeImage,
    handleImageSize,
    isOpen,
    enlargedImageSize,
    breakpointId,
    setToolTipMessage,
    builderLabel
  } = _ref;
  const [isCheckboxSelected, setIsCheckboxSelected] = useState(isDecorative);
  const [value, setValue] = useState(alt ?? '');
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    state: {
      altTextDraftDataMap
    },
    actions: {
      setAltTextDraftDataMap
    }
  } = useAltTextScreenContext();
  useEffect(() => {
    setValue(alt ?? '');
  }, [alt]);
  const itemType = appDefinitionId ? mapAppDefinitionIdToItemType(appDefinitionId) : undefined;
  const setMap = oldMap => setAltTextDraftDataMap == null ? void 0 : setAltTextDraftDataMap(new Map(oldMap));
  const debouncedSetMap = useDebounceFn(setMap, 300);
  const handleChange = useCallback((newStatus, newValue, updatedDecorative) => {
    if (newValue !== undefined) {
      setValue(newValue);
    }
    altTextDraftDataMap == null || altTextDraftDataMap.set(id, {
      alt: newValue ?? value,
      breakpointId,
      builderLabel,
      entityId,
      id,
      isDecorative: updatedDecorative ?? isCheckboxSelected,
      status: newStatus,
      tpaData
    });
    debouncedSetMap(altTextDraftDataMap);
  }, [altTextDraftDataMap, breakpointId, debouncedSetMap, entityId, id, isCheckboxSelected, tpaData, value, builderLabel]);
  const enlargedImageHeight = (enlargedImageSize == null ? void 0 : enlargedImageSize.height) <= 84 ? 84 : 150;
  const {
    status: formStatus,
    statusMessage
  } = getFormStatusAndStatusMessage(t, status, value) ?? {};
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP1"
  }, isOpen && /*#__PURE__*/React.createElement(EnlargedImage, {
    dataHook: dataHook,
    enlargedImageHeight: enlargedImageHeight,
    enlargedImageSize: enlargedImageSize,
    enlargeImage: enlargeImage,
    handleImageSize: handleImageSize,
    index: index,
    status: status
  }), /*#__PURE__*/React.createElement(Collapse, {
    dataHook: dataHooks.altImageRuleContentCollapse(dataHook),
    open: !isCheckboxSelected
  }, /*#__PURE__*/React.createElement(FormField, {
    dataHook: dataHooks.altImageRuleContentFormField(dataHook),
    label: t('accessibility-wizard.rules-list.alt-image.content.input.label'),
    labelSize: "tiny",
    status: formStatus,
    statusMessage: statusMessage,
    suffix: /*#__PURE__*/React.createElement(GenerateWithAiSuffixButton, {
      entityId: entityId,
      handleChange: handleChange,
      id: id,
      index: index,
      setToolTipMessage: setToolTipMessage,
      status: status,
      url: url
    })
  }, /*#__PURE__*/React.createElement(InputArea, {
    dataHook: dataHooks.altImageRuleContentInputArea(dataHook),
    disabled: showLoader || status === ElementStatus.Loading,
    maxLength: MAX_ALT_TEXT_LENGTH,
    placeholder: t('accessibility-wizard.rules-list.alt-image.content.input.placeholder'),
    rows: 3,
    size: "small",
    value: value,
    autoFocus: true,
    autoSelect: true,
    onBlur: () => {
      if (value !== alt) {
        biLogger.elementFieldChange({
          fieldName: BiFieldNames.AltText,
          index,
          status,
          value
        });
      }
    },
    onChange: e => {
      const newValue = e.target.value;
      const newStatus = convertBooleanStatusToElementStatus(getAlternativeTextCardReason(newValue) === undefined);
      handleChange(newStatus, newValue);
    }
  })), /*#__PURE__*/React.createElement(Box, {
    marginTop: "SP1"
  }, isTextInImage && /*#__PURE__*/React.createElement(TextInImageMessage, {
    elementIndex: index,
    elementStatus: status
  })), /*#__PURE__*/React.createElement(Box, {
    marginTop: isTextInImage ? 'SP1' : 0
  }, /*#__PURE__*/React.createElement(RuleHelperLayout, null, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "thin",
    secondary: true
  }, /*#__PURE__*/React.createElement(Trans, {
    components: {
      bold: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "bold",
        secondary: true
      })
    },
    i18nKey: getTransKeyByItemType('accessibility-wizard.rules-list.alt-image.tip.1', itemType)
  }))))), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2",
    marginTop: "3PX"
  }, enableDecorativeImages && !tpaData && /*#__PURE__*/React.createElement(Checkbox, {
    checked: isCheckboxSelected,
    dataHook: dataHooks.altImageRuleContentCheckbox(dataHook),
    disabled: showLoader || status === ElementStatus.Loading,
    maxLines: 2,
    size: "small",
    vAlign: "top",
    onChange: () => {
      const newDecorativeState = !isCheckboxSelected;
      const updatedStatus = convertBooleanStatusToElementStatus(newDecorativeState || getAlternativeTextCardReason(value) === undefined);
      handleChange(updatedStatus, value, newDecorativeState);
      setIsCheckboxSelected(newDecorativeState);
      biLogger.elementFieldChange({
        fieldName: BiFieldNames.Checkbox,
        index,
        status,
        value: newDecorativeState ? 'on' : 'off'
      });
    }
  }, /*#__PURE__*/React.createElement(Box, {
    marginTop: "3PX"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    secondary: true
  }, t('accessibility-wizard.rules-list.alt-image.checkbox.label'))))));
};
//# sourceMappingURL=alt-image-rule-content.js.map